import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Tooltip,
  Chip,
  CircularProgress,
  Alert,
  Paper,
  Slider,
  LinearProgress,
} from '@mui/material';
import {
  Close as CloseIcon,
  Download as DownloadIcon,
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
  VolumeUp as VolumeUpIcon,
  VolumeOff as VolumeOffIcon,
  Fullscreen as FullscreenIcon,
  Refresh as RefreshIcon,
  Replay as ReplayIcon,
  SkipPrevious as SkipPreviousIcon,
  SkipNext as SkipNextIcon,
  Videocam as VideoIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';

interface VideoViewerProps {
  open: boolean;
  onClose: () => void;
  videoUrl: string;
  fileName: string;
  fileSize?: number;
  duration?: number;
  width?: number;
  height?: number;
  thumbnailUrl?: string;
}

const VideoViewer: React.FC<VideoViewerProps> = ({
  open,
  onClose,
  videoUrl,
  fileName,
  fileSize,
  duration,
  width,
  height,
  thumbnailUrl,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fullscreen, setFullscreen] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [volume, setVolume] = useState(1);
  const [muted, setMuted] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [showControls, setShowControls] = useState(true);
  const [retryCount, setRetryCount] = useState(0);
  
  const videoRef = useRef<HTMLVideoElement>(null);
  const controlsTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Reset state when component opens
  useEffect(() => {
    if (open) {
      setError(null);
      setFullscreen(false);
      setPlaying(false);
      setCurrentTime(0);
      setVolume(1);
      setMuted(false);
      setPlaybackRate(1);
      setShowControls(true);
      setRetryCount(0);
    }
  }, [open]);

  // Handle video events
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleLoadStart = () => setLoading(true);
    const handleLoadedData = () => setLoading(false);
    const handleError = () => {
      setError('Failed to load video');
      setLoading(false);
    };
    const handleTimeUpdate = () => setCurrentTime(video.currentTime);
    const handlePlay = () => setPlaying(true);
    const handlePause = () => setPlaying(false);
    const handleEnded = () => setPlaying(false);

    video.addEventListener('loadstart', handleLoadStart);
    video.addEventListener('loadeddata', handleLoadedData);
    video.addEventListener('error', handleError);
    video.addEventListener('timeupdate', handleTimeUpdate);
    video.addEventListener('play', handlePlay);
    video.addEventListener('pause', handlePause);
    video.addEventListener('ended', handleEnded);

    return () => {
      video.removeEventListener('loadstart', handleLoadStart);
      video.removeEventListener('loadeddata', handleLoadedData);
      video.removeEventListener('error', handleError);
      video.removeEventListener('timeupdate', handleTimeUpdate);
      video.removeEventListener('play', handlePlay);
      video.removeEventListener('pause', handlePause);
      video.removeEventListener('ended', handleEnded);
    };
  }, [videoUrl]);

  // Auto-hide controls
  useEffect(() => {
    if (playing) {
      const timeout = setTimeout(() => {
        setShowControls(false);
      }, 3000);
      controlsTimeoutRef.current = timeout;
    } else {
      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current);
      }
      setShowControls(true);
    }

    return () => {
      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current);
      }
    };
  }, [playing]);

  const handleDownload = async () => {
    try {
      const response = await fetch(videoUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading video:', error);
    }
  };

  const handleRetry = () => {
    setRetryCount(prev => prev + 1);
    setError(null);
    setLoading(true);
  };

  const handlePlayPause = () => {
    const video = videoRef.current;
    if (!video) return;

    if (playing) {
      video.pause();
    } else {
      video.play();
    }
  };

  const handleSeek = (_: Event, newValue: number | number[]) => {
    const video = videoRef.current;
    if (!video) return;

    const newTime = newValue as number;
    video.currentTime = newTime;
    setCurrentTime(newTime);
  };

  const handleVolumeChange = (_: Event, newValue: number | number[]) => {
    const video = videoRef.current;
    if (!video) return;

    const newVolume = newValue as number;
    video.volume = newVolume;
    setVolume(newVolume);
    setMuted(newVolume === 0);
  };

  const handleMuteToggle = () => {
    const video = videoRef.current;
    if (!video) return;

    if (muted) {
      video.volume = volume;
      setMuted(false);
    } else {
      video.volume = 0;
      setMuted(true);
    }
  };

  const handlePlaybackRateChange = (rate: number) => {
    const video = videoRef.current;
    if (!video) return;

    video.playbackRate = rate;
    setPlaybackRate(rate);
  };

  const handleFullscreen = () => {
    const video = videoRef.current;
    if (!video) return;

    if (!document.fullscreenElement) {
      video.requestFullscreen();
      setFullscreen(true);
    } else {
      document.exitFullscreen();
      setFullscreen(false);
    }
  };

  const formatTime = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    
    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getAspectRatio = (): string => {
    if (width && height) {
      const ratio = width / height;
      if (ratio > 1.5) return '16:9';
      if (ratio > 1.3) return '4:3';
      if (ratio > 1.1) return '5:4';
      return '1:1';
    }
    return '16:9';
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={fullscreen ? false : "lg"}
      fullWidth={!fullscreen}
      fullScreen={fullscreen}
      PaperProps={{
        sx: {
          borderRadius: fullscreen ? 0 : 3,
          background: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)',
          boxShadow: '0 20px 40px rgba(0,0,0,0.3)',
          maxHeight: fullscreen ? '100vh' : '90vh',
        }
      }}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.9 }}
        transition={{ duration: 0.2 }}
      >
        <DialogTitle sx={{ 
          pb: 1,
          background: 'rgba(0, 0, 0, 0.8)',
          backdropFilter: 'blur(10px)',
          borderBottom: '1px solid rgba(255,255,255,0.1)'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ 
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 48,
                height: 48,
                borderRadius: 2,
                background: 'linear-gradient(45deg, #ff6b6b 30%, #ee5a24 90%)',
                color: 'white',
              }}>
                <VideoIcon />
              </Box>
              <Box>
                <Typography variant="h6" fontWeight="bold" color="white">
                  {fileName}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip
                    label="Video File"
                    size="small"
                    sx={{ 
                      background: 'rgba(255, 107, 107, 0.2)',
                      color: '#ff6b6b',
                      border: '1px solid rgba(255, 107, 107, 0.3)'
                    }}
                  />
                  {fileSize && (
                    <Typography variant="caption" color="rgba(255,255,255,0.7)">
                      {formatFileSize(fileSize)}
                    </Typography>
                  )}
                  {duration && (
                    <Typography variant="caption" color="rgba(255,255,255,0.7)">
                      • {formatTime(duration)}
                    </Typography>
                  )}
                  {width && height && (
                    <Typography variant="caption" color="rgba(255,255,255,0.7)">
                      • {width}×{height} ({getAspectRatio()})
                    </Typography>
                  )}
                </Box>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Tooltip title="Refresh Video">
                <IconButton
                  onClick={handleRetry}
                  disabled={loading}
                  sx={{ 
                    color: 'rgba(255,255,255,0.7)',
                    '&:hover': { 
                      background: 'rgba(255,255,255,0.1)',
                      transform: 'scale(1.1)'
                    },
                    transition: 'all 0.2s ease'
                  }}
                >
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title={fullscreen ? "Exit Fullscreen" : "Fullscreen"}>
                <IconButton
                  onClick={handleFullscreen}
                  sx={{ 
                    color: 'rgba(255,255,255,0.7)',
                    '&:hover': { 
                      background: 'rgba(255,255,255,0.1)',
                      transform: 'scale(1.1)'
                    },
                    transition: 'all 0.2s ease'
                  }}
                >
                  <FullscreenIcon />
                </IconButton>
              </Tooltip>
              <IconButton onClick={onClose} sx={{ 
                color: 'rgba(255,255,255,0.7)',
                '&:hover': { 
                  background: 'rgba(255,255,255,0.1)',
                  transform: 'scale(1.1)'
                },
                transition: 'all 0.2s ease'
              }}>
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 0, background: '#000' }}>
          {error ? (
            <Box sx={{ p: 3 }}>
              <Alert 
                severity="error" 
                sx={{ mb: 2 }}
                action={
                  retryCount < 3 ? (
                    <Button color="inherit" size="small" onClick={handleRetry}>
                      Retry
                    </Button>
                  ) : null
                }
              >
                {error}
                {retryCount >= 3 && (
                  <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                    Maximum retry attempts reached. Please try downloading the file instead.
                  </Typography>
                )}
              </Alert>
            </Box>
          ) : (
            <Box sx={{ position: 'relative', background: '#000' }}>
              {loading && (
                <Box sx={{ 
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  zIndex: 2,
                  display: 'flex', 
                  flexDirection: 'column',
                  alignItems: 'center', 
                  gap: 2
                }}>
                  <CircularProgress size={40} color="primary" />
                  <Typography variant="body1" color="white">
                    Loading video...
                  </Typography>
                </Box>
              )}
              
              <Box
                sx={{
                  position: 'relative',
                  width: '100%',
                  height: fullscreen ? '100vh' : '60vh',
                  background: '#000',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: playing ? 'none' : 'pointer',
                  '&:hover .video-controls': {
                    opacity: 1,
                  }
                }}
                onMouseMove={() => setShowControls(true)}
                onMouseLeave={() => {
                  if (playing) {
                    const timeout = setTimeout(() => setShowControls(false), 3000);
                    controlsTimeoutRef.current = timeout;
                  }
                }}
                onClick={handlePlayPause}
              >
                <video
                  ref={videoRef}
                  src={videoUrl}
                  poster={thumbnailUrl}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',
                  }}
                  preload="metadata"
                />
                
                {/* Video Controls Overlay */}
                <Box
                  className="video-controls"
                  sx={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    background: 'linear-gradient(transparent, rgba(0,0,0,0.8))',
                    opacity: showControls ? 1 : 0,
                    transition: 'opacity 0.3s ease',
                    p: 2,
                  }}
                >
                  {/* Progress Bar */}
                  <Box sx={{ mb: 2 }}>
                    <Slider
                      value={currentTime}
                      onChange={handleSeek}
                      min={0}
                      max={duration || 0}
                      step={0.1}
                      sx={{
                        color: '#ff6b6b',
                        '& .MuiSlider-thumb': {
                          width: 16,
                          height: 16,
                        },
                        '& .MuiSlider-track': {
                          height: 4,
                        },
                        '& .MuiSlider-rail': {
                          height: 4,
                          opacity: 0.3,
                        },
                      }}
                    />
                  </Box>
                  
                  {/* Control Buttons */}
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between',
                    gap: 2
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <IconButton onClick={handlePlayPause} sx={{ color: 'white' }}>
                        {playing ? <PauseIcon /> : <PlayIcon />}
                      </IconButton>
                      
                      <Typography variant="body2" color="white" sx={{ minWidth: '80px' }}>
                        {formatTime(currentTime)} / {formatTime(duration || 0)}
                      </Typography>
                    </Box>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <IconButton onClick={handleMuteToggle} sx={{ color: 'white' }}>
                        {muted ? <VolumeOffIcon /> : <VolumeUpIcon />}
                      </IconButton>
                      
                      <Slider
                        value={muted ? 0 : volume}
                        onChange={handleVolumeChange}
                        min={0}
                        max={1}
                        step={0.1}
                        sx={{
                          width: 80,
                          color: '#ff6b6b',
                        }}
                      />
                      
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        {[0.5, 1, 1.25, 1.5, 2].map((rate) => (
                          <Button
                            key={rate}
                            size="small"
                            variant={playbackRate === rate ? 'contained' : 'outlined'}
                            onClick={() => handlePlaybackRateChange(rate)}
                            sx={{
                              minWidth: 'auto',
                              px: 1,
                              py: 0.5,
                              fontSize: '0.75rem',
                              color: playbackRate === rate ? 'white' : 'rgba(255,255,255,0.7)',
                              borderColor: 'rgba(255,255,255,0.3)',
                              '&:hover': {
                                borderColor: 'rgba(255,255,255,0.5)',
                              }
                            }}
                          >
                            {rate}x
                          </Button>
                        ))}
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ 
          p: 3, 
          background: 'rgba(0, 0, 0, 0.8)',
          backdropFilter: 'blur(10px)',
          borderTop: '1px solid rgba(255,255,255,0.1)'
        }}>
          <Button
            onClick={onClose}
            variant="outlined"
            sx={{
              borderRadius: 2,
              px: 3,
              py: 1,
              fontWeight: 'bold',
              textTransform: 'none',
              color: 'white',
              borderColor: 'rgba(255,255,255,0.3)',
              '&:hover': {
                borderColor: 'rgba(255,255,255,0.5)',
              }
            }}
          >
            Close
          </Button>
          <Button
            onClick={handleDownload}
            variant="contained"
            startIcon={<DownloadIcon />}
            sx={{
              background: 'linear-gradient(45deg, #ff6b6b 30%, #ee5a24 90%)',
              borderRadius: 2,
              px: 3,
              py: 1,
              fontWeight: 'bold',
              textTransform: 'none',
              '&:hover': {
                background: 'linear-gradient(45deg, #ee5a24 30%, #ff6b6b 90%)',
                transform: 'translateY(-2px)',
                boxShadow: '0 8px 20px rgba(0,0,0,0.3)'
              },
              transition: 'all 0.3s ease'
            }}
          >
            Download Video
          </Button>
        </DialogActions>
      </motion.div>
    </Dialog>
  );
};

export default VideoViewer;
